import React, { useEffect, useMemo, useState } from 'react'
import 'reactflow/dist/base.css'
import { ReactComponent as DownloadIcon } from '../../../../../../images/icons/action_download.svg'
import {
  Button,
  Table,
  DateRangePicker,
  StatsCard,
  Info,
  ChevronLeft,
  TabMenu,
} from '@demandlocal/components'
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useLocation,
  Link,
  useSearchParams,
  useNavigate,
} from 'react-router-dom'
import { formatNumber } from '../../../../../../lib/utils/formatNumber'
import { useGetThirdPartyAudienceById } from '../../../../../../api/dealership/customers/audiences/useGetThirdPartyAudienceById'
import PolarAreaChart from '../../../../../../components/Charts/PolarAreaChart'
import { take } from 'lodash'
import HorizontalBarChart from '../../../../../../components/Charts/HorizontalBarChart'
import { useDownloadPDFReport } from '../../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { GA4 } from '../../../../../../lib/utils/ga4'
import mtm from '../../../../../../lib/utils/mtm'
import LineChart from '../../../../../../components/Charts/LineChart'
import {
  AudienceMetrics,
  useGetThirdPartyAudienceReport,
} from '../../../../../../api/dealership/customers/audiences/useGetThirdPartyAudienceReport'
import BarChart from '../../../../../../components/Charts/BarChart'
import { VendorAudience } from '../../../../../../lib/types/Audience'
import { useGetThirdPartyAudienceConversions } from '../../../../../../api/dealership/customers/audiences/useGetThirdPartyAudienceConversions'
import { ColumnDef, createColumnHelper, DisplayColumnDef } from '@tanstack/react-table'
import { useDealership } from '../../../../../../store/zustand/dealershipStore'
import { DateRange } from '../../../../../../lib/types/DateRange'
import { useDateRange } from '../../../../../../store/zustand/dateRangeStore'
import PageHeader from '../../../../../../components/PageHeader/PageHeader'
import { AnimatePresence, motion } from 'framer-motion'
import { useGetTagCodes } from '../../../../../../api/utils/useGetTagCodes'
import MultiLineChart from '../../../../../../components/Charts/MultiLineChart'
import { useTranslation } from 'react-i18next'
import { useGetDealershipConversionGoalsByDealershipID } from '../../../../../../api/dealership/audiences/useGetConversionGoalsByDealershipID'

const columnHelper = createColumnHelper<{
  campaign: string
  client_campaign_id: number
  product_code: string
  creative: string
  creative_size: string
  impressions: number
  clicks: number
  views: number
  total_conversions: number
}>()

type Location = {
  name: string
  relevanceScore: number
}

const AudienceTemplate = (): JSX.Element => {
  const { audience_id } = useParams()
  const location = useLocation()
  const reportType = location.pathname.split('/').pop()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const dealership = useDealership()

  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange, setDateRange } = useDateRange()

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const {
    data: audience,
    isLoading,
    isPreviousData,
  } = useGetThirdPartyAudienceById(audience_id)
  const {
    data: audienceMetrics,
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReport,
  } = useGetThirdPartyAudienceReport({
    audience_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })
  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  useEffect(() => {
    if (!isLoadingReport) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport])

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  return (
    <div className={'flex flex-1 flex-col h-full gap-3 pb-10'} id={'content'}>
      <div className="flex flex-row justify-between px-10 py-5">
        <div className="flex flex-row gap-3 items-center">
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.preventDefault()
              navigate(t('dealership.audiences.programmatic', { dealership: dealership }))
            }}>
            {/* @ts-ignore */}
            <ChevronLeft className="fill-web-gray" />
          </div>
          <h2 className="font-bold text-xl">{audience?.vendor_audience_name}</h2>
        </div>
        <AnimatePresence>
          <motion.div className="flex flex-row gap-3 items-center">
            <div className={'print:hidden flex flex-row gap-2 items-center'}>
              {reportType === 'data' && (
                <DateRangePicker filters={filters} onChange={onDateRangeChange} />
              )}
              <Button
                disabled={isLoading || isDownloadingReport}
                leftSlot={<DownloadIcon className={'fill-white'} />}
                animate={false}
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Generating report...')
                  GA4.sendEvent({
                    event_name: 'export_pdf',
                    event_params: {
                      campaign_type: 'ttd_audience',
                      campaign_id: audience?.audience_id,
                    },
                  })
                  mtm.sendEvent('Export', 'PDF', 'ttd_audience', audience?.audience_id)
                  await downloadReport(undefined, {
                    async onSuccess(data) {
                      toast.success('Report generated successfully', { id: toastId })
                      const blob = await data.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `${audience?.vendor_audience_name}_insights.pdf`
                      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                      a.click()
                      a.remove()
                    },
                  })
                }}>
                Export as PDF
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="px-10">
        <TabMenu
          options={[
            {
              to: 'data',
              name: 'Audience Data',
            },
            {
              to: 'insights',
              name: 'Audience Insights',
            },
          ]}
        />
      </div>
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mb-10'} />
        <Routes>
          <Route
            path="insights"
            element={
              <AudienceInsights
                audience={audience}
                isLoading={isLoading}
                isPreviousData={isPreviousData}
              />
            }
          />
          <Route
            path={'data'}
            element={
              <AudienceData
                filters={filters}
                audienceMetrics={audienceMetrics}
                isLoadingReport={isLoadingReport}
                isPreviousReport={isPreviousReport}
              />
            }
          />
          <Route path="*" element={<Navigate to={'data'} replace />} />
        </Routes>
      </div>
    </div>
  )
}

const AudienceInsights = ({
  audience,
  isLoading,
  isPreviousData,
}: {
  audience?: VendorAudience
  isLoading: boolean
  isPreviousData: boolean
}): JSX.Element => {
  return (
    <div className={'flex flex-col gap-10 pb-8'}>
      <motion.div
        layout={'preserve-aspect'}
        className={`grid grid-cols-3 gap-5 min-w-max w-full `}>
        <StatsCard
          title={'Active IDs'}
          content={formatNumber(audience?.vendor_audience_details?.activeIds?.total || 0)}
        />
        <StatsCard
          title={'Households'}
          content={formatNumber(audience?.vendor_audience_details?.householdCount || 0)}
        />
        <StatsCard
          title={'People'}
          content={formatNumber(audience?.vendor_audience_details?.personsCount || 0)}
        />
      </motion.div>
      <div className={'flex flex-col gap-8'}>
        <div className={'flex grid grid-cols-1 gap-5'}>
          <div className={'min-h-[500px] border-2 rounded-xl'}>
            <div className={'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic'}>
              <span>Interests</span>
            </div>
            <div className={'h-[400px] p-2'}>
              <HorizontalBarChart
                data={
                  take(
                    audience?.vendor_audience_details?.interests?.map(
                      ({ interestName, relevanceScore }) => {
                        return {
                          label: interestName,
                          value: Math.round(relevanceScore * 10) / 10,
                        }
                      },
                    ),
                    15,
                  ) ?? []
                }
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
        <div className={'flex grid grid-cols-2 gap-5'}>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
              <span>Devices</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                data={
                  audience?.vendor_audience_details?.devices
                    ?.map(
                      (device: { deviceType: string; percentageOfAudience: number }) => {
                        return {
                          label: device.deviceType,
                          value: device.percentageOfAudience,
                        }
                      },
                    )
                    .filter(({ value }) => value > 1)
                    .sort(
                      (a: { value: number }, b: { value: number }) => b.value - a.value,
                    ) ?? []
                }
                isLoading={isLoading}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
              <span>Channels</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                data={audience?.vendor_audience_details?.channels
                  ?.map((channels: { channel: string; percentageOfAudience: number }) => {
                    return {
                      label: channels.channel,
                      value: channels.percentageOfAudience,
                    }
                  })
                  .filter(({ value }) => value > 1)
                  .sort(
                    (a: { value: number }, b: { value: number }) => b.value - a.value,
                  )}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
        <div className={'flex grid grid-cols-3 gap-5'}>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
              <span>Top Metros</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <HorizontalBarChart
                data={take(
                  audience?.vendor_audience_details?.locations?.filter(
                    ({ geoType }) => geoType === 'Metro',
                  ),
                  10,
                )
                  ?.map((location) => {
                    const loc = location as Location
                    return {
                      label: loc.name.replace('United States - ', ''),
                      value: Math.round(loc.relevanceScore * 10) / 10,
                    }
                  })
                  .filter(({ value }) => value > 1)}
                isLoading={isLoading}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
              <span>Top Regions</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <HorizontalBarChart
                data={take(
                  audience?.vendor_audience_details?.locations?.filter(
                    ({ geoType }) => geoType === 'Region',
                  ),
                  10,
                )
                  ?.map((location) => {
                    const loc = location as Location
                    return {
                      label: loc.name.replace('United States - ', ''),
                      value: Math.round(loc.relevanceScore * 10) / 10,
                    }
                  })
                  .filter(({ value }) => value > 1)}
                isLoading={isLoading}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={'pt-5 pb-2 px-5 flex  font-bold text-sm text-primary-xiketic'}>
              <span>Top Cities</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <HorizontalBarChart
                data={take(
                  audience?.vendor_audience_details?.locations?.filter(
                    ({ geoType }) => geoType === 'City',
                  ),
                  10,
                )
                  ?.map((location) => {
                    const loc = location as Location
                    return {
                      label: loc.name.replace('United States - ', ''),
                      value: Math.round(loc.relevanceScore * 10) / 10,
                    }
                  })
                  .filter(({ value }) => value > 1)}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const AudienceData = ({
  filters,
  audienceMetrics,
  isLoadingReport,
  isPreviousReport,
}: {
  filters: DateRange
  audienceMetrics?: AudienceMetrics
  isLoadingReport: boolean
  isPreviousReport: boolean
}): JSX.Element => {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const { data: tag_names } = useGetDealershipConversionGoalsByDealershipID()
  const { data: tag_codes } = useGetTagCodes({ pageSize: 100 })
  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor('campaign', {
        header: () => 'Campaign',
        cell: (info) => (
          <Link
            className={'font-bold text-xs text-primary-persian-blue'}
            to={t('dealership.campaigns.report', {
              dealership,
              campaign: {
                product_code: info.row.original.product_code,
                client_campaign_id: info.row.original.client_campaign_id,
                start_date: filters.dateRange.startDate,
                end_date: filters.dateRange.endDate,
              },
            })}>
            {info.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor('creative', {
        header: () => 'Creative',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('impressions', {
        header: () => 'Impressions',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('clicks', {
        header: () => 'Clicks',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('views', {
        header: () => 'Views',
        cell: (info) => info.getValue(),
      }),
    ] as ColumnDef<
      {
        campaign: string
        client_campaign_id: number
        product_code: string
        creative: string
        creative_size: string
        impressions: number
        clicks: number
        views: number
        total_conversions: number
      },
      unknown
    >[]
    if (tag_names) {
      columns.push(
        columnHelper.display({
          header: () => 'All Conversions',
          id: 'all_conversions',
          cell: (info) => {
            const conversions = tag_codes?.data
              ?.filter((tag) => tag.id !== 4)
              ?.reduce((acc, cur) => acc + (info.row.original[cur?.tag_name] ?? 0), 0)

            return conversions
          },
        }),
        columnHelper.display({
          header: () => {
            return (
              <div className="flex flex-row items-center gap-2">
                <span>Conversion rate</span>
                <Info message="Percentage of users that engage with an ad and complete a conversion action, which is calculated by dividing the total number of conversions by the number of visitors to a website" />
              </div>
            )
          },
          id: 'conversion_rate',
          cell: (info) => {
            // const conversions = tag_names
            //   ?.filter((tag) => tag.id !== 4)
            //   ?.reduce((acc, cur) => acc + info.row.original[cur.tag_name], 0)
            const conversions = tag_codes?.data
              ?.filter((tag) => tag.id !== 4)
              ?.reduce((acc, cur) => acc + (info.row.original[cur.tag_name] ?? 0), 0)

            return `${
              Math.round(
                !Boolean(info.row.original['All Page Views'])
                  ? 0
                  : (1000 * (conversions ?? 0)) /
                      Math.max(info.row.original['All Page Views'], conversions ?? 1),
              ) / 10
            }%`
          },
        }),
        ...tag_names.map((tag) => {
          return columnHelper.display({
            header: () => {
              return (
                <div className="flex flex-row gap-1 items-center">{tag.tag_name}</div>
              )
            },
            id: tag.tag_name,
            cell: (info) => info.row.original[tag.tag_name],
          })
        }),
      )
    }

    return columns
  }, [
    dealership,
    filters.dateRange.endDate,
    filters.dateRange.startDate,
    t,
    tag_codes?.data,
    tag_names,
  ])

  return (
    <div className={'flex flex-col gap-10 pb-8'}>
      <motion.div
        layout={'preserve-aspect'}
        className={`grid grid-cols-3 gap-5 min-w-max w-full `}>
        <StatsCard
          title={'Total Impressions'}
          content={formatNumber(
            audienceMetrics?.dailyMetrics?.reduce(
              (acc, cur) => (acc += cur.impressions),
              0,
            ) || 0,
          )}
        />
        <StatsCard
          title={'Total Clicks'}
          content={formatNumber(
            audienceMetrics?.dailyMetrics?.reduce((acc, cur) => (acc += cur.clicks), 0) ||
              0,
          )}
        />
        <StatsCard
          title={'Total views'}
          content={formatNumber(
            audienceMetrics?.dailyMetrics?.reduce((acc, cur) => (acc += cur.views), 0) ||
              0,
          )}
        />
      </motion.div>
      <div className={'flex flex-col gap-8'}>
        <div className="flex grid grid-cols-2 gap-5">
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div className={'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic'}>
              <span>Total Impressions</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={audienceMetrics?.dailyMetrics ?? []}
                isLoading={isLoadingReport}
                isPreviousData={isPreviousReport}
                gradient={'green-to-cyan'}
                yKey={'impressions'}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div className={'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic'}>
              <span>Total views</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <LineChart
                data={audienceMetrics?.dailyMetrics ?? []}
                isLoading={isLoadingReport}
                isPreviousData={isPreviousReport}
                gradient={'blue-to-purple'}
                yKey={'views'}
              />
            </div>
          </div>
        </div>
        <div className="flex grid grid-cols-2 gap-5">
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div className={'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic'}>
              <span>Campaigns</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <PolarAreaChart
                isLoading={isLoadingReport}
                data={
                  audienceMetrics?.campaignMetrics?.map((d) => ({
                    label: d.campaign,
                    value: d.impressions,
                  })) ?? []
                }
                isPreviousData={isPreviousReport}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Impressions by day of week</span>
            </div>
            <div className={'h-[270px] p-2'}>
              <BarChart
                data={
                  audienceMetrics?.dayOfWeekData?.map(({ dayOfWeek, impressions }) => {
                    return {
                      label: dayOfWeek,
                      value: impressions,
                    }
                  }) ?? []
                }
                isLoading={isLoadingReport}
                isPreviousData={isPreviousReport}
              />
            </div>
          </div>
        </div>
        <div className="flex grid grid-cols-2 gap-5">
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
              }>
              <span>Daily reach</span>
              <Info
                message={
                  'The total number of unique households, people, or devices who were exposed to an ad'
                }
              />
            </div>
            <div className={'h-[270px] p-2'}>
              <MultiLineChart
                data={
                  audienceMetrics?.dailyMetrics?.flatMap((d) => {
                    return [
                      {
                        date: d.date,
                        type: 'Households',
                        reach: d.household_reach,
                      },
                      { date: d.date, type: 'People', reach: d.persons_reach },
                      { date: d.date, type: 'Devices', reach: d.ids_reach },
                    ]
                  }) ?? []
                }
                customOptions={{
                  elements: {
                    line: {
                      tension: 0,
                    },
                    point: {
                      radius: 0,
                    },
                  },
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                  },
                }}
                gradient={['blue-to-purple', 'red-to-orange', 'green-to-cyan']}
                borderColor="white"
                groupKey={'type'}
                labelKey={'type'}
                yKey={'reach'}
                xKey={'date'}
              />
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
              }>
              <span>Daily frequency</span>
              <Info
                message={
                  'Average number of times a household, person, or device was exposed to an ad'
                }
              />
            </div>
            <div className={'h-[270px] p-2'}>
              <MultiLineChart
                data={
                  audienceMetrics?.dailyMetrics?.flatMap((d) => {
                    return [
                      {
                        date: d.date,
                        type: 'Household',
                        frequency: d.household_frequency,
                      },
                      { date: d.date, type: 'Person', frequency: d.persons_frequency },
                      { date: d.date, type: 'Device', frequency: d.ids_frequency },
                    ]
                  }) ?? []
                }
                customOptions={{
                  fill: false,
                  elements: {
                    line: {
                      tension: 0,
                    },
                    point: {
                      radius: 0,
                    },
                  },
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                  },
                }}
                gradient={['blue-to-purple', 'red-to-orange', 'green-to-cyan']}
                borderColor="white"
                groupKey={'type'}
                labelKey={'type'}
                yKey={'frequency'}
                xKey={'date'}
                showBackground={false}
                borderWidth={2}
              />
            </div>
          </div>
        </div>
        <div className={'flex flex-col gap-4'}>
          <span className={'font-bold text-primary-xiketic text-sm'}>
            Audience performance
          </span>
          <Table
            emptyLabel={'No data found'}
            columns={columns}
            filters={{
              audience_id: audienceMetrics?.audience_id,
              start_date: filters?.dateRange?.startDate,
              end_date: filters?.dateRange?.endDate,
            }}
            defaultSorting={[{ id: 'campaign', desc: true }]}
            fetchFn={useGetThirdPartyAudienceConversions}
          />
        </div>
      </div>
    </div>
  )
}

export default AudienceTemplate
